import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../../../services/useAuth";
import { useReportFilters } from "../shared/useReportFilters";
import { toISO } from "../shared/utils";
import { API_URL } from "../shared/constants";

export function useOEEReport({
  autoFetch = true,
  stopReasonsPage,
  stopReasonsPageSize,
  machineBreakdownPage,
  machineBreakdownPageSize,
} = {}) {
  const { getToken } = useAuth();
  const filters = useReportFilters();

  const [machines, setMachines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [teepData, setTeepData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toasts, setToasts] = useState([]);

  const authH = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  const addToast = useCallback(
    (msg, type = "success") =>
      setToasts((p) => [...p, { id: Date.now(), message: msg, type }]),
    [],
  );

  const dismissToast = useCallback(
    (id) => setToasts((p) => p.filter((t) => t.id !== id)),
    [],
  );

  useEffect(() => {
    (async () => {
      try {
        const [mR, sR] = await Promise.all([
          axios.get(`${API_URL}/api/machines`, authH()),
          axios.get(`${API_URL}/api/shifts`, authH()),
        ]);
        setMachines(mR.data.machines || mR.data || []);
        setShifts(sR.data.shifts || []);
      } catch {}
    })();
  }, [authH]);

  const fetchData = useCallback(
    async (overrides = {}) => {
      setLoading(true);
      try {
        const p = new URLSearchParams();
        p.set("startDate", toISO(filters.startDate, false));
        p.set("endDate", toISO(filters.endDate, true));
        if (filters.machineIds.length)
          p.set("machineIds", filters.machineIds.join(","));
        if (filters.shiftId) p.set("shiftId", filters.shiftId);

        const reasonsPage =
          overrides.stopReasonsPage ?? stopReasonsPage ?? null;
        const reasonsPageSize =
          overrides.stopReasonsPageSize ?? stopReasonsPageSize ?? null;
        if (reasonsPage != null)
          p.set("stopReasonsPage", String(reasonsPage));
        if (reasonsPageSize != null)
          p.set("stopReasonsPageSize", String(reasonsPageSize));

        const mbPage =
          overrides.machineBreakdownPage ?? machineBreakdownPage ?? null;
        const mbPageSize =
          overrides.machineBreakdownPageSize ?? machineBreakdownPageSize ?? null;
        if (mbPage != null) p.set("machineBreakdownPage", String(mbPage));
        if (mbPageSize != null)
          p.set("machineBreakdownPageSize", String(mbPageSize));

        const r = await axios.get(`${API_URL}/api/teep?${p}`, authH());
        setTeepData(r.data.data);
      } catch {
        addToast("OEE/TEEP verisi yüklenemedi", "danger");
        setTeepData(null);
      } finally {
        setLoading(false);
      }
    },
    [
      filters.startDate,
      filters.endDate,
      filters.machineIds,
      filters.shiftId,
      stopReasonsPage,
      stopReasonsPageSize,
      machineBreakdownPage,
      machineBreakdownPageSize,
      authH,
      addToast,
    ],
  );

  useEffect(() => {
    if (autoFetch) fetchData();
  }, []); // eslint-disable-line

  return {
    filters,
    machines,
    shifts,
    teepData,
    loading,
    fetchData,
    toasts,
    dismissToast,
    authH,
  };
}
